<script lang="ts" setup>
import { destructurePromise } from '@sortera/ee-utils'
import { computed, ref, useTemplateRef, watch } from 'vue'

import { useVModel } from '@/utils/use-vue/index.ts'

export type AutofocusElements = 'firstButton' | 'noFocus' | 'default'

export interface UiDialogProps {
  isOpen: boolean
  autofocusElement?: AutofocusElements
  position?: Position
}

export type UiDialogEmits = {
  'update:isOpen': [value: boolean]
  /** Emits after dialog closing animation is fully done */
  didClose: []
  clickOutside: []
}

interface Slots {
  default(slotProps: SlotProps): unknown
}

type Position = 'center' | 'top' | 'bottom'

interface SlotProps {
  closeAndWaitTillAnimated: () => Promise<void>
}

const props = withDefaults(defineProps<UiDialogProps>(), {
  autofocusElement: 'default',
  position: 'center',
})

const emit = defineEmits<UiDialogEmits>()
defineSlots<Slots>()
const slotProps = computed((): SlotProps => ({ closeAndWaitTillAnimated }))

defineOptions({
  inheritAttrs: false,
})

const CLASS_BY_POSITION: Record<Position, string> = {
  center: 'modal-middle',
  top: 'modal-top',
  bottom: 'modal-bottom',
}

const modalRef = useTemplateRef('modal')

// We need to remove the dialog from the DOM after it's closed.
// Otherwise if there are multiple dialogs on the page, strange bugs can happen,
// like changes done in one leaking into another.
const shouldRender = ref(props.isOpen)

const isOpenModel = useVModel(props, 'isOpen', emit)

watch(
  () => [isOpenModel.value, modalRef.value],
  () => {
    // needs to come first so that modalRef is set
    if (isOpenModel.value) shouldRender.value = true
    if (!modalRef.value) return
    if (isOpenModel.value) {
      shouldRender.value = true
      modalRef.value.showModal()
      destructuredClosingAnimationPromise = makeDestructuredPromise() // reset promise

      if (props.autofocusElement === 'firstButton') {
        const firstButton = modalRef.value?.querySelector('button')
        firstButton?.focus()
      }
    } else {
      modalRef.value?.close()
    }
  },
  { immediate: true, deep: true }
)

let destructuredClosingAnimationPromise = makeDestructuredPromise()

function makeDestructuredPromise(): [Promise<void>, () => void] {
  const [promise, resolve] = destructurePromise()

  // eslint-disable-next-line promises/prefer-await-to-then
  promise.then(() => {
    shouldRender.value = false
  })
  return [promise, resolve] as const
}

function onTransitionEnd(event: Event): void {
  if (event.target !== modalRef.value) return // Ignore events from children
  if (isOpenModel.value) return // Ignore events when opening
  emit('didClose')

  const [, resolve] = destructuredClosingAnimationPromise
  resolve()
}

/** Meant to be used in scoped slot. Will wait for closing animation to finish, if there is any. */
function closeAndWaitTillAnimated(): Promise<void> {
  isOpenModel.value = false
  const [promise] = destructuredClosingAnimationPromise
  return promise
}

function onClickOutside(): void {
  emit('clickOutside')
}
</script>

<template>
  <Teleport to="body">
    <dialog
      v-if="shouldRender"
      v-bind="$attrs"
      ref="modal"
      class="modal"
      :class="[CLASS_BY_POSITION[props.position]]"
      @cancel.prevent="isOpenModel = false /** Circumvent default closing of dialog when ESC is clicked */"
      @transitionend="onTransitionEnd"
    >
      <div
        class="modal-backdrop"
        @click.prevent="onClickOutside"
      />
      <div class="modal-box">
        <template v-if="props.autofocusElement === 'noFocus'">
          <!-- hidden focus trap. Can not have `display: none`, or it won't get focus in hosted environments -->
          <button
            autofocus
            class="h-0 w-0 opacity-0"
          />
        </template>
        <slot v-bind="slotProps" />
      </div>
    </dialog>
  </Teleport>
</template>
